import {
	ReactElement,
	RefObject,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	SearchInputForm,
	TableView,
	Pagination,
	DateString,
	ContextMenu,
	computeContextMenuTitle,
	useContextMenu,
	SearchPlaceholder,
	TableViewNoResultConfig
} from "client/Components";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import {
	createLocalDataSource,
	SelectableItemOptions,
	useSelection,
	useSortHandler
} from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import { UserRequestClientModel } from "client/Api/UserRequests/manageUserRequests";
import { UserRequestStatus } from "client/Api/UserRequests/userRequest";
import { RequestedItem } from "./RequestedItem";
import styles from "./UserRequestsTabContent.module.less";
import {
	ManageUserRequestActionContext,
	ManageUserRequestsAction,
	OPEN_ACTION_NAME
} from "./actions";
import { getUserRequestItemName, getUserRequestKindText } from "./common";
import { ensureNotNil, generateTrackingId } from "client/Misc/utils";
import { SettingsPage } from "../SettingsPage";
import { contains } from "client/Data/searching";
import { TimeZone } from "client/DateAndTime/timezones";
import { parseDtoDate } from "client/DateAndTime/dateTimeUtils";
import { ActionKind } from "client_legacy/ndaw/actions";

type RequestDataSourceContext = {
	hasResponse: boolean;
};

const useDataSource = createLocalDataSource<
	UserRequestGridItem,
	undefined,
	RequestDataSourceContext
>()
	.setConfiguration({
		search: {
			doesMatchItem: (item, searchTerm, context) => {
				return context.hasResponse
					? handledSearchMatchFunction(item, searchTerm)
					: pendingSearchMatchFunction(item, searchTerm);
			}
		},
		hasPagination: true
	})
	.getHook();


const {
	Row,
	Cell
} = TableView;

export type UserRequestsTabContentProps = {
	kind: UserRequestStatus;
	items: readonly UserRequestGridItem[];
	isMobile: boolean;
	selection: readonly UserRequestGridItem[];
	onSelectionChange(selection: readonly UserRequestGridItem[]): void;
	actions: readonly ManageUserRequestsAction[];
	timezone: TimeZone;
};

export const UserRequestsTabContent = (props: UserRequestsTabContentProps): ReactElement => {
	const {
		kind,
		items,
		isMobile,
		selection,
		onSelectionChange,
		actions,
		timezone
	} = props;

	const hasResponse: boolean = kind !== UserRequestStatus.Open;

	const tableRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
	const [sortingState, setSortingState] = useState<DataSourceSortableState<UserRequestGridItem>>({
		propertyName: hasResponse
			? "respondDate"
			: "requestDate",
		direction: DataSourceSortDirection.Descending
	});
	const { sortingFor } = useSortHandler<UserRequestGridItem>(sortingState, setSortingState);
	const [searchTerm, setSearchTerm] = useState<string>("");

	useEffect(
		() => setCurrentPageNumber(0),
		[searchTerm, sortingState]
	);

	const { currentItems, pagination } = useDataSource(
		items,
		{
			sorting: sortingState,
			pagination: {
				currentPageNumber
			},
			searchTerm,
			globalContext: {
				hasResponse
			}
		}
	);

	const { multiSelection, selectionFor } = useSelection(
		currentItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems: selection,
			setSelectedItems: onSelectionChange
		}
	);

	const [onContextMenu, contextMenuProps] = useContextMenu(tableRef);

	const actionContext = useMemo(
		(): ManageUserRequestActionContext => ({
			isMobile,
			selection: selection.map(x => x.request)
		}),
		[isMobile, selection]
	);

	const executeHandler = (item: UserRequestGridItem): void => {
		const openAction: ManageUserRequestsAction | undefined = actions.find(x => x.name === OPEN_ACTION_NAME);
		if (!openAction || openAction.kind !== ActionKind.Regular) {
			return;
		}
		const { request } = item;
		const context: ManageUserRequestActionContext = {
			isMobile,
			selection: [request]
		};

		if (openAction.canExecute(context)) {
			openAction.execute(context);
		}
	};

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig => {
			if (searchTerm) {
				return {
					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message: localize("_PleaseTrySearchingWithAnotherTerm")
				};
			}

			return {
				icon: "circle-question",
				title: localize("_NoRequestsYet"),
				message: localize(userRequestStatusToEmptyKeyMap[kind])
			};
		},
		[kind, searchTerm]
	);

	return (
		<div className={styles.root}>
			<SettingsPage.SearchSection>
				<SearchInputForm
					onChange={setSearchTerm}
					trackingId="search-term"
					height="large"
					icon="filter"
					placeholder={(
						<SearchPlaceholder
							kind="filter"
							properties={[
								localize("_RequestedItem"),
								localize("_Type"),
								localize("_RequestedBy"),
								localize("_Email")
							]}
						/>
					)}
				/>
			</SettingsPage.SearchSection>

			<SettingsPage.Section height="stretch">

				<TableView
					multiSelectable={multiSelection}
					trackingId="user-request-grid"
					items={currentItems}
					itemKey="id"
					columns={[
						{
							title: localize("_RequestedItem"),
							sortable: sortingFor("requestedItemName"),
							width: 200,
							trackingId: "requested-item"
						},
						{
							title: localize("_Type"),
							sortable: sortingFor("type"),
							width: 200,
							trackingId: "type"
						},
						{
							title: localize("_RequestedBy"),
							sortable: sortingFor("requestedBy"),
							width: 200,
							trackingId: "requested-by"
						},
						{
							title: localize("_Email"),
							sortable: sortingFor("email"),
							width: 200,
							trackingId: "email"
						},
						{
							title: localize("_RequestDate"),
							sortable: sortingFor("requestDate"),
							width: 200,
							trackingId: "request-date"
						},
						hasResponse && {
							title: localize("_RespondDate"),
							sortable: sortingFor("respondDate"),
							width: 200,
							trackingId: "response-date"
						},
						hasResponse && {
							title: localize("_RespondedBy"),
							sortable: sortingFor("respondedBy"),
							width: 200,
							trackingId: "responded-by"
						}
					]}
					ref={tableRef}
					height="stretch"
					noResultConfig={noResultConfig}
				>
					{item => {
						const selectableOptions: SelectableItemOptions = selectionFor(item);

						return (
							<Row
								selectable={selectableOptions}
								trackingId={item.request.id.toString()}
								onExecute={() => executeHandler(item)}
								onContextMenu={onContextMenu}
							>
								<Cell>
									<RequestedItem
										isSelected={selectableOptions && selectableOptions.isSelected}
										item={item.request.item}
									/>
								</Cell>
								<Cell>{item.type}</Cell>
								<Cell>{item.requestedBy}</Cell>
								<Cell>{item.email}</Cell>
								<Cell>
									<DateString
										value={item.requestDate}
										format="DMYT"
										timezone={timezone}
									/>
								</Cell>
								{ hasResponse && (
									<>
										<Cell>
											<DateString
												value={ensureNotNil(item.respondDate)}
												format="DMYT"
												timezone={timezone}
											/>
										</Cell>
										<Cell>{item.respondedBy}</Cell>
									</>
								)}
							</Row>
						);
					}}
				</TableView>
				<Pagination {...pagination} onPageNumberChanged={setCurrentPageNumber} />
				{ contextMenuProps && (
					<ContextMenu
						{...contextMenuProps}
						actions={actions}
						context={actionContext}
						trackingId={generateTrackingId("user-request-overview-context-menu", `|${kind}`)}
						title={computeContextMenuTitle(
							actionContext.selection,
							(x) => x.item
								.name
						)}
					/>
				)}
			</SettingsPage.Section>
		</div>
	);
};
UserRequestsTabContent.displayName = "UserRequestsTabContent";

export type UserRequestGridItem = {
	id: number;
	requestedItemName: string;
	type: string;
	requestedBy: string;
	email: string | null;
	requestDate: Date;
	respondDate: Date | undefined;
	respondedBy: string | undefined;
	request: UserRequestClientModel;
};


function pendingSearchMatchFunction(
	item: UserRequestGridItem,
	normalizedNotEmptySearchTerm: Exclude<string, "">
): boolean {
	if (contains(item.requestedItemName, normalizedNotEmptySearchTerm)
		|| contains(item.type, normalizedNotEmptySearchTerm)
		|| contains(item.requestedBy, normalizedNotEmptySearchTerm)
		|| contains(item.email ?? "", normalizedNotEmptySearchTerm)
	) {
		return true;
	}

	return false;
}

function handledSearchMatchFunction(
	item: UserRequestGridItem,
	normalizedNotEmptySearchTerm: Exclude<string, "">
): boolean {
	if (contains(item.requestedItemName, normalizedNotEmptySearchTerm)
		|| contains(item.type, normalizedNotEmptySearchTerm)
		|| contains(item.requestedBy, normalizedNotEmptySearchTerm)
		|| contains(item.email ?? "", normalizedNotEmptySearchTerm)
		|| contains(item.respondedBy, normalizedNotEmptySearchTerm)
	) {
		return true;
	}

	return false;
}

const userRequestStatusToEmptyKeyMap: Record<UserRequestStatus, LocalizationKey> = {
	[UserRequestStatus.Open]: "_ThereAreNoOpenRequestsYet",
	[UserRequestStatus.Accepted]: "_ThereAreNoAcceptedRequestsYet",
	[UserRequestStatus.Declined]: "_ThereAreNoDeclinedRequestsYet"
};

export function convertToGridItem(model: UserRequestClientModel): UserRequestGridItem {
	const {
		id,
		createdOn,
		requester,
		respondedOn,
		responder,
		item
	} = model;

	return {
		id,
		requestedItemName: getUserRequestItemName(item),
		requestedBy: requester.name,
		email: requester.email,
		requestDate: parseDtoDate(createdOn),
		respondDate: parseDtoDate(respondedOn) ?? undefined,
		respondedBy: responder?.name,
		type: getUserRequestKindText(model.item.kind),
		request: model
	};
}
